import { useSurveyAuditsBySubject, useUnarchiveAudit } from './useSurveyAudit';

/**
 * Creates data management hooks for ARCHIVED status survey audits
 * These are audits that have been archived and hidden from active view
 *
 * @param subjectId - Subject ID for scoping audits
 * @param tenantId - Tenant ID (for context, may not be used directly)
 * @param domainId - Domain ID (for context, may not be used directly)
 */
export const createArchivedAuditsDataManagementHooks = (
  subjectId: string,
  _tenantId: string,
  _domainId: string
) => {
  /**
   * Query hook: Get ARCHIVED audits from backend
   * Backend enriches with surveyTitle, responseCount, and questionCount
   */
  const useArchivedAuditsQuery = () => {
    const query = useSurveyAuditsBySubject(subjectId, {
      status: 'ARCHIVED',
      enabled: !!subjectId,
    });

    return {
      ...query,
      // Direct passthrough - types already match due to alias
      data: query.data?.data?.audits || [],
      refetch: query.refetch,
      isFetching: query.isFetching,
    };
  };

  /**
   * Unarchive audit mutation wrapper
   * Used to restore an ARCHIVED audit to READY status
   */
  const useUnarchiveAuditWrapper = () => {
    const unarchiveMutation = useUnarchiveAudit();

    return {
      mutateAsync: async (params: { auditId: string; subjectId: string }): Promise<void> => {
        await unarchiveMutation.mutateAsync(params);
      },
      isPending: unarchiveMutation.isPending,
    };
  };

  return {
    useArchivedAuditsQuery,
    useUnarchiveAuditWrapper,
  };
};
